import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InfoBox } from "@/components/InfoBox";
import type { ModalProps } from "@/components/Modal";
import { Modal } from "@/components/Modal";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { handleNonGlobalApiError } from "@/features/error/handleError";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ModalFooter } from "@/pages/applications/pages/customFields/components/ModalFooter";
import { sharedStyles } from "@/pages/applications/pages/customFields/components/sharedStyles";
import { SingleSelectOptionEditor } from "@/pages/applications/pages/customFields/components/SingleSelectOptionEditor";
import { TypeBadge } from "@/pages/applications/pages/customFields/components/TypeBadge";
import {
  CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH,
  CUSTOM_FIELD_NAME_MAX_LENGTH,
} from "@/pages/applications/pages/customFields/constants";
import type { SingleSelectOptionsDraft } from "@/pages/applications/pages/customFields/utils/singleSelectOptions";
import {
  createOptionsDraft,
  emptyOptionsDraft,
  flattenOptionsDraft,
  isOptionsDraftValid,
  toPatchOptions,
} from "@/pages/applications/pages/customFields/utils/singleSelectOptions";

type CustomField = InterludeTypes["CustomField"];

interface FormValues {
  name: string;
  description: string;
  options: SingleSelectOptionsDraft;
}

function isSingleSelectField(field: CustomField): boolean {
  return field.type.expected && field.type.value === "single_select";
}

function toOptionsDraft(field: CustomField | null): SingleSelectOptionsDraft {
  if (!field?.options) return emptyOptionsDraft;
  return createOptionsDraft(field.options);
}

/**
 * Whether the response actually carried an option list.
 *
 * `options` is `.nullable().optional()`, and the two absences mean different
 * things: `null` is the backend saying this field has no options, `undefined`
 * is the backend not answering -- which is what a caller that has not opted in
 * to single select receives. Editing on the strength of the second would put
 * an empty list over whatever is really stored, so the editor stays shut.
 */
function hasLoadedOptions(field: CustomField | null): boolean {
  return field?.options !== undefined;
}

interface EditFieldModalProps {
  open: boolean;
  field: CustomField | null;
  onClose: () => void;
  onSuccess: () => void;
}

const styles = {
  keyDisplay: {
    display: "flex",
    alignItems: "flex-start",
    padding: "8px 12px",
    backgroundColor: theme.colors.staticBgPlain,
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
    fontSize: "14px",
    color: theme.colors.staticFgNote,
    minHeight: "40px",
    boxSizing: "border-box",
    flex: 1,
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  infoIcon: {
    fontSize: "14px",
    color: theme.colors.staticFgPlaceholder,
    cursor: "help",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const EditFieldModal: React.FC<EditFieldModalProps> =
  function EditFieldModal({ open, field, onClose, onSuccess }) {
    const { t } = useTranslation();
    const [apiError, setApiError] = useState<string | null>(null);

    const {
      control,
      handleSubmit,
      reset,
      watch,
      formState: { isValid, isDirty },
    } = useForm<FormValues>({
      mode: "onChange",
      defaultValues: {
        name: field?.name ?? "",
        description: field?.description ?? "",
        options: toOptionsDraft(field),
      },
    });

    /**
     * Seeds the form from the field, and reseeds it whenever the field
     * arrives new -- which is how a save that lost the race recovers: the
     * failed mutation invalidates the list, the refetch brings back the other
     * editor's version, and this puts it in the form.
     *
     * The error is cleared only when a *different* field is opened. A reseed
     * of the same field is the recovery itself, and clearing then would take
     * away the one message explaining why the user's edits just vanished.
     */
    const seededFieldRef = useRef<CustomField | null>(null);
    /**
     * Where the form stands with respect to a save that lost its race.
     *
     * `"reloading"` is set the moment the failure comes back and means the
     * reload has not landed yet. `"following"` means it has, and the form is
     * now showing the server's copy rather than the user's work, so it keeps
     * showing the server's copy until they start again.
     *
     * The two stages exist because `isDirty` cannot be asked the question in
     * the first one. Losing a race usually happens *because* the user edited
     * something, so the form is dirty exactly when the reload matters most;
     * gating the first reseed on cleanliness kept the user's stale list under
     * a message saying it had been replaced. After that first reseed `reset`
     * has made the form clean again, and from there dirtiness does mean what
     * it needs to mean: the user has started over, and nothing may overwrite
     * them again.
     *
     * State rather than a ref, which is what makes the reseed reachable at
     * all: setting a ref does not re-render, and this effect only reruns on
     * its dependencies. When the concurrent edit had already landed in the
     * cache before the save, the refetch that follows the failure returns
     * identical data, React Query keeps the object identity, `field` never
     * changes -- and the effect never ran again. The form kept the user's
     * stale list under a message saying it had been reloaded.
     *
     * Tracking rather than clearing on the first new `field` is for a
     * different reason: identity alone cannot say whether that first one is
     * the reload. A background refetch can arrive between the seed and the
     * failure, so the version present at the failure is sometimes the one the
     * save was made against and sometimes the one that beat it.
     */
    const [recovery, setRecovery] = useState<"off" | "reloading" | "following">(
      "off",
    );
    /**
     * The whole of what the form was seeded from, kept beside the form rather
     * than derived from `field`.
     *
     * A background refetch changes `field` without reseeding (the effect above
     * only reseeds on a different field or a lost race, so it cannot overwrite
     * what the user is typing) and a baseline derived from `field` would move
     * anyway. Anything the other editor changed would then read as a local
     * change and go back out under the user's copy, putting their edit back
     * with no error: for options every id is still present, so the stale check
     * passes, and name and description are not checked at all.
     *
     * All three travel together because they drift together. Holding only the
     * options was the first version of this fix and left the other two wrong.
     */
    const [seeded, setSeeded] = useState<FormValues>(() => ({
      name: field?.name ?? "",
      description: field?.description ?? "",
      options: toOptionsDraft(field),
    }));
    useEffect(() => {
      if (!field) {
        seededFieldRef.current = null;
        setRecovery("off");
        return;
      }

      // The ref says which field the form was seeded from; its id answers
      // whether a different field has been opened.
      const isDifferentField = seededFieldRef.current?.id !== field.id;
      // While recovering, every version the server sends replaces the form.
      // The first one lands whatever the user had typed, because that is the
      // work the failure already discarded; after it, `reset` has cleaned the
      // form, so `isDirty` means they have started over and tracking stops.
      const hasNewData = seededFieldRef.current !== field;
      const trackingNewData =
        hasNewData &&
        (recovery === "reloading" || (recovery === "following" && !isDirty));

      // Reseeding on every new `field` would have made a background refetch
      // (React Query refetches on window focus) silently replace whatever the
      // user had typed. So it happens only when a different field is opened,
      // or when a lost race asked for it and its reload has landed.
      if (isDifferentField || trackingNewData) {
        const next = {
          name: field.name,
          description: field.description,
          options: toOptionsDraft(field),
        };
        reset(next);
        seededFieldRef.current = field;
        setSeeded(next);
        setRecovery(isDifferentField ? "off" : "following");
      }

      if (isDifferentField) setApiError(null);
    }, [field, reset, recovery, isDirty]);

    const watchedName = watch("name");
    const watchedDescription = watch("description");
    const watchedOptions = watch("options");

    // Both halves are required: the type says this field has options, and
    // `hasLoadedOptions` says the response actually carried them.
    const isSingleSelect =
      field !== null && isSingleSelectField(field) && hasLoadedOptions(field);

    const originalOptions = seeded.options;

    /**
     * Whether the option list differs from what was seeded, read off the
     * payload that would actually be sent.
     *
     * Three things count as a change and all three have to: an option whose
     * content moved, so its entry carries more than `{ id }`; an option added
     * or removed, so the length differs; and a pure reorder, since the array
     * order *is* the sort order. The last one is why this compares ids
     * positionally rather than only asking whether any entry grew -- three
     * untouched options dragged into a new order still produce three
     * single-key entries.
     */
    const optionsChanged = useMemo(() => {
      if (!isSingleSelect) return false;
      const patch = toPatchOptions(watchedOptions, originalOptions);
      const seededIds = flattenOptionsDraft(originalOptions).map(
        (option) => option.id,
      );
      if (patch.length !== seededIds.length) return true;
      return patch.some((option, index) => {
        if (Object.keys(option).length > 1) return true;
        return !("id" in option) || option.id !== seededIds[index];
      });
    }, [isSingleSelect, watchedOptions, originalOptions]);

    /**
     * The gap between a lost race and the reload that answers it, which is one
     * refetch wide.
     *
     * The form still holds what the user typed and the message above it
     * already says that copy is gone, so leaving it editable invites them to
     * carry on in a version about to be replaced. Typing there would also stop
     * the replacement, since an edit is what ends the recovery.
     */
    const isReloading = recovery === "reloading";

    const nameChanged = watchedName.trim() !== seeded.name;
    const descriptionChanged = watchedDescription.trim() !== seeded.description;
    const hasChanges = nameChanged || descriptionChanged || optionsChanged;

    const updateMutation = interlude.customField.useUpdateCustomField(
      {
        params: {
          fieldId: field?.id ?? 0,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: async (error) => {
          await handleNonGlobalApiError(error, {
            CUSTOM_FIELD_NAME_DUPLICATE() {
              setApiError(t("customField.error.nameDuplicate"));
            },
          });
        },
      },
    );

    const patchMutation = interlude.customField.usePatchCustomField(
      {
        params: {
          fieldId: field?.id ?? 0,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: async (error) => {
          await handleNonGlobalApiError(error, {
            CUSTOM_FIELD_NAME_DUPLICATE() {
              setApiError(t("customField.error.nameDuplicate"));
            },
            /**
             * Someone changed this field's options while this editor was
             * open. There is no merge and no version token -- the contract is
             * last write wins, and the loser re-reads.
             *
             * The reload needs no code here: a failed mutation in this api
             * still invalidates the api's queries when it settles, so the
             * list refetches and the `field` prop arrives new, which reseeds
             * the form through the effect above. All that is left is telling
             * the user, because their unsaved edits are gone and a silent
             * reseed looks like the form threw the work away by itself.
             */
            CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_STALE() {
              // Arms the reseed rather than doing it here: the reload has not
              // arrived yet, and reseeding now would put the same stale data
              // back and then ignore the fresh data when it lands.
              setRecovery("reloading");
              setApiError(t("customField.error.optionsStale"));
            },
            /**
             * Reaching this almost always means the list moved, not that the
             * user typed a duplicate: the editor already disables submit
             * while two names match, archived ones included, so a duplicate
             * of their own making never leaves the form. What does get here
             * is a race -- another editor renaming an option to the name
             * this save introduces collides without any option id going
             * missing, so the stale check passes and this fires instead.
             *
             * Hence the same reseed as a stale write, which covers both
             * origins on its own: it only runs when the refetch brings back
             * different data, so a race replaces the list with the colliding
             * name now visible, and anything else leaves the form alone.
             */
            CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_DUPLICATE() {
              setRecovery("reloading");
              setApiError(
                // A key of its own rather than the create modal's
                // `optionsDuplicate`. The two say different things: creating
                // a field can only collide with a name in the same request,
                // while here the collision is with the server's copy and the
                // list below has just been replaced with it. One published
                // string cannot carry both, and the wrong half is worse than
                // no half -- telling a create user their list was reloaded
                // points at something that did not happen.
                t("customField.error.optionsDuplicateReloaded"),
              );
            },
          });
        },
      },
    );

    /**
     * Every writable part of the form, shut for the same reason in two
     * different moments: while a save is in flight, because success closes the
     * modal and resets, so anything typed meanwhile lands nowhere; and while
     * the reload after a lost race is on its way, because that copy is already
     * spoken for.
     */
    const isFormLocked =
      updateMutation.isPending || patchMutation.isPending || isReloading;

    const handleClose = useHandler(() => {
      reset();
      // The page keeps one modal and swaps the field into it, so a request
      // this form started would otherwise still be describing the next one.
      // That locks a form with nothing to do with it until the old call
      // settles, or until the network comes back.
      updateMutation.reset();
      patchMutation.reset();
      setApiError(null);
      onClose();
    });

    const handleModalClose = useHandler<ModalProps["handleClose"]>(
      (_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        handleClose();
      },
    );

    const onSubmit = useHandler((values: FormValues) => {
      if (!field) return;
      setApiError(null);

      if (!isSingleSelect) {
        // The other four types keep the older whole-field PUT, which is still
        // supported. Moving them onto PATCH is a separate change with no
        // benefit to them.
        updateMutation.mutate({
          name: values.name.trim(),
          description: values.description.trim(),
        });
        return;
      }

      // A PATCH carrying none of its three keys is rejected outright, so a
      // save that changes nothing must not be sent. The submit button is
      // already disabled in that state; this is the second guard, for a
      // submit that arrives some other way.
      if (!hasChanges) {
        handleClose();
        return;
      }

      // Every key is optional and an absent one leaves that part of the field
      // alone. Sending all three would put the form's copy of the other two
      // back over whatever another editor changed in the meantime, with no
      // error to show for it -- so only what moved goes out.
      patchMutation.mutate({
        ...(nameChanged ? { name: values.name.trim() } : {}),
        ...(descriptionChanged
          ? { description: values.description.trim() }
          : {}),
        ...(optionsChanged
          ? { options: toPatchOptions(values.options, originalOptions) }
          : {}),
      });
    });

    const footer: React.ReactNode = useMemo(() => {
      // Built before the `!field` early return below, so without this the
      // work is done and then thrown away on every render with no field.
      if (!field) return null;
      return (
        <ModalFooter
          onCancel={handleClose}
          onSubmit={handleSubmit(onSubmit)}
          cancelKey="common.cancel"
          submitKey="customField.editModal.submit"
          isValid={
            isValid &&
            hasChanges &&
            (!isSingleSelect || isOptionsDraftValid(watchedOptions))
          }
          // Both, not just `isLoading`. That one only stops the pointer, so
          // a focused button still submits on Enter, sending a second save
          // against the copy the first one is replacing.
          disabled={isFormLocked}
          isLoading={isFormLocked}
        />
      );
    }, [
      handleClose,
      handleSubmit,
      onSubmit,
      isValid,
      hasChanges,
      isSingleSelect,
      watchedOptions,
      field,
      isFormLocked,
    ]);

    if (!field) {
      return null;
    }

    return (
      <Modal
        open={open}
        handleClose={handleModalClose}
        title={t("customField.editModal.title")}
        width={520}
        footer={footer}
      >
        <Stack gap="16px" paddingBottom="8px">
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("customField.editModal.description")}
          </Typography>

          {apiError ? (
            <InfoBox
              variant="error"
              title={t("customField.error.configurationFailed")}
              description={apiError}
            />
          ) : null}

          {/* Name Field */}
          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("customField.createModal.name.label")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
            </Box>
            <Controller
              name="name"
              control={control}
              rules={{
                required: t("customField.error.nameRequired"),
                maxLength: CUSTOM_FIELD_NAME_MAX_LENGTH,
                validate: (value) =>
                  value.trim().length > 0 ||
                  t("customField.error.nameRequired"),
              }}
              render={({ field: formField, fieldState: { error } }) => (
                <TextField
                  {...formField}
                  disabled={isFormLocked}
                  onChange={(e) => {
                    // Auto-trim leading whitespace while preserving middle spaces
                    const value = e.target.value;
                    const trimmedValue = value.trimStart();
                    formField.onChange({
                      ...e,
                      target: { ...e.target, value: trimmedValue },
                    });
                  }}
                  onBlur={(e) => {
                    // Trim trailing whitespace on blur
                    const trimmedValue = e.target.value.trimEnd();
                    if (trimmedValue !== e.target.value) {
                      formField.onChange({
                        ...e,
                        target: { ...e.target, value: trimmedValue },
                      });
                    }
                    formField.onBlur();
                  }}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    htmlInput: {
                      maxLength: CUSTOM_FIELD_NAME_MAX_LENGTH,
                    },
                    input: {
                      endAdornment: (
                        <Box component="span" sx={sharedStyles.charCount}>
                          <Typography variant="note" color="inherit">
                            {watchedName.length}/{CUSTOM_FIELD_NAME_MAX_LENGTH}
                          </Typography>
                        </Box>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Key Field - Locked */}
          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("customField.createModal.key.label")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
              <Tooltip title={t("customField.editModal.key.hint")} arrow>
                <Box component="span" sx={styles.infoIcon}>
                  <MotifIcon un-i-motif="circle_info" />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={sharedStyles.keyInputWrapper}>
              <Box component="span" sx={styles.keyDisplay}>
                {field.key}
              </Box>
            </Box>
            <FormHelperText>
              {t("customField.editModal.key.locked")}
            </FormHelperText>
          </Box>

          {/* Type Field - Locked */}
          <Box sx={sharedStyles.formSection}>
            <Box sx={sharedStyles.label}>
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgBody}
              >
                {t("customField.createModal.type.label")}
              </Typography>
              <Box component="span" sx={sharedStyles.required}>
                *
              </Box>
            </Box>
            <Box>
              <TypeBadge fieldType={field.type} />
            </Box>
          </Box>

          {/* Option list, single select only. The editor renders its own
              "Options" label and the n/200 counter, so there is no label
              wrapper here. Never gated: a field of this type can exist while
              the creation gate is off, and a field nobody can edit is worse
              than one they can. */}
          {isSingleSelect ? (
            <Controller
              name="options"
              control={control}
              render={({ field: optionsField }) => (
                <SingleSelectOptionEditor
                  value={optionsField.value}
                  onChange={optionsField.onChange}
                  disabled={isFormLocked}
                />
              )}
            />
          ) : null}

          {/* Description Field */}
          <Box sx={sharedStyles.formSection}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgBody}
            >
              {t("customField.createModal.description.label")}
            </Typography>
            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH,
              }}
              render={({ field: formField, fieldState: { error } }) => (
                <TextField
                  {...formField}
                  disabled={isFormLocked}
                  sx={sharedStyles.descriptionInput}
                  fullWidth
                  multiline
                  rows={3}
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    htmlInput: {
                      maxLength: CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH,
                    },
                    input: {
                      endAdornment: (
                        <Box
                          component="span"
                          sx={sharedStyles.charCountWithPadding}
                        >
                          <Typography variant="note" color="inherit">
                            {watchedDescription.length}/
                            {CUSTOM_FIELD_DESCRIPTION_MAX_LENGTH}
                          </Typography>
                        </Box>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Modal>
    );
  };
